import { Fragment, useEffect, useMemo, useState } from "react";
import { Loader2, Save, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import {
  PERMISSION_KEYS,
  PERMISSION_LABELS,
  moduleLabel,
  type PermissionKey,
  type PermissionMatrixModule,
  type RolePermission,
} from "../types";
import {
  usePermissionModules,
  useRolePermissions,
  useUpdateRolePermissions,
} from "../hooks/useRoles";

interface PermissionsMatrixProps {
  roleId: string;
  roleName: string;
  isReadOnly?: boolean;
}

function emptyPermission(module: string): RolePermission {
  return {
    module,
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canAdmin: false,
  };
}

export function PermissionsMatrix({
  roleId,
  roleName,
  isReadOnly = false,
}: PermissionsMatrixProps) {
  const { data, isLoading, isError } = useRolePermissions(roleId);
  const {
    data: groups,
    isLoading: isLoadingModules,
    isError: isErrorModules,
  } = usePermissionModules();
  const update = useUpdateRolePermissions(roleId);

  const [rows, setRows] = useState<Record<string, RolePermission>>({});
  const [moduleFilter, setModuleFilter] = useState<string>("");
  const [savedFlash, setSavedFlash] = useState(false);

  // Flat, registry-ordered list of permission-bearing modules.
  const allModules = useMemo<PermissionMatrixModule[]>(
    () => (groups ?? []).flatMap((g) => g.modules),
    [groups],
  );
  const moduleKeys = useMemo(
    () => allModules.map((m) => m.module),
    [allModules],
  );

  useEffect(() => {
    if (!data || moduleKeys.length === 0) return;
    const map: Record<string, RolePermission> = {};
    for (const m of moduleKeys) {
      const found = data.find((p) => p.module === m);
      map[m] = found ?? emptyPermission(m);
    }
    setRows(map);
  }, [data, moduleKeys]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return moduleKeys.some((m) => {
      const orig = data.find((p) => p.module === m) ?? emptyPermission(m);
      const curr = rows[m];
      if (!curr) return false;
      return PERMISSION_KEYS.some((k) => orig[k] !== curr[k]);
    });
  }, [rows, data, moduleKeys]);

  function toggleCell(module: string, key: PermissionKey) {
    if (isReadOnly) return;
    setRows((prev) => ({
      ...prev,
      [module]: {
        ...(prev[module] ?? emptyPermission(module)),
        [key]: !(prev[module] ?? emptyPermission(module))[key],
      },
    }));
  }

  function setRowAll(module: string, value: boolean) {
    if (isReadOnly) return;
    setRows((prev) => ({
      ...prev,
      [module]: {
        module,
        canView: value,
        canCreate: value,
        canEdit: value,
        canDelete: value,
        canExport: value,
        canAdmin: value,
      },
    }));
  }

  async function handleSave() {
    if (isReadOnly || !dirty) return;
    const payload = moduleKeys.map((m) => rows[m] ?? emptyPermission(m));
    await update.mutateAsync(payload);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2200);
  }

  if (isLoading || isLoadingModules) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-border bg-card py-12 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
        Loading permissions…
      </div>
    );
  }

  if (isError || isErrorModules) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load permissions.
      </div>
    );
  }

  const visibleGroups = (groups ?? [])
    .map((g) => ({
      ...g,
      modules: moduleFilter
        ? g.modules.filter((m) => m.module === moduleFilter)
        : g.modules,
    }))
    .filter((g) => g.modules.length > 0);

  const renderRow = (module: string, label: string) => {
    const row = rows[module] ?? emptyPermission(module);
    const allChecked = PERMISSION_KEYS.every((k) => row[k]);
    return (
      <tr key={module} className="hover:bg-[hsl(210,50%,98%)]">
        <td className="py-3 pl-10 pr-6 font-medium text-foreground">{label}</td>
        {PERMISSION_KEYS.map((k) => (
          <td key={k} className="px-3 py-3 text-center">
            <div className="flex justify-center">
              <Checkbox
                checked={row[k]}
                disabled={isReadOnly}
                onCheckedChange={() => toggleCell(module, k)}
                aria-label={`${label} ${PERMISSION_LABELS[k]}`}
              />
            </div>
          </td>
        ))}
        <td className="px-3 py-3 text-right">
          <button
            type="button"
            onClick={() => setRowAll(module, !allChecked)}
            disabled={isReadOnly}
            className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {allChecked ? "Deselect" : "Select all"}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="w-full space-y-4">
      {isReadOnly && (
        <div
          className="flex items-start gap-2 rounded-lg border px-4 py-3 text-sm"
          style={{
            background: "hsl(210,100%,97%)",
            borderColor: "hsl(210,80%,85%)",
            color: "hsl(210,80%,28%)",
          }}
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <div>
            <div className="font-medium">Read-only role</div>
            <div className="text-[12px] opacity-90">
              Super Admin has full access to all modules. Permissions are read-only.
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Role</span>
            <Select value={roleId} disabled className="w-48">
              <option value={roleId}>{roleName}</option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Module</span>
            <Select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="w-52"
            >
              <option value="">All Modules</option>
              {allModules.map((m) => (
                <option key={m.module} value={m.module}>
                  {m.label || moduleLabel(m.module)}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {savedFlash && (
            <span className="text-xs text-emerald-600">Saved</span>
          )}
          <Button
            onClick={handleSave}
            disabled={isReadOnly || !dirty || update.isPending}
          >
            {update.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <Save className="h-4 w-4" strokeWidth={1.5} />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Module</th>
              {PERMISSION_KEYS.map((k) => (
                <th key={k} className="px-3 py-3 text-center">
                  {PERMISSION_LABELS[k]}
                </th>
              ))}
              <th className="px-3 py-3 text-right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visibleGroups.length === 0 ? (
              <tr>
                <td
                  colSpan={PERMISSION_KEYS.length + 2}
                  className="px-6 py-8 text-center text-sm text-muted-foreground"
                >
                  No modules to display.
                </td>
              </tr>
            ) : (
              visibleGroups.map((group) => (
                <Fragment key={group.name ?? "_ungrouped"}>
                  {group.label && (
                    <tr className="bg-[hsl(210,30%,97%)]">
                      <td
                        colSpan={PERMISSION_KEYS.length + 2}
                        className="px-6 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        {group.label}
                      </td>
                    </tr>
                  )}
                  {group.modules.map((m) =>
                    renderRow(m.module, m.label || moduleLabel(m.module)),
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
